/**
 * @file
 * Enhanced table functionality with fullscreen, zoom, and accessibility features.
 */

((Drupal, once) => {
  Drupal.behaviors.enhancedTables = {
    attach: (context, settings) => {
      const tables = once('enhanced-table', 'table', context);

      for (const table of tables) {
        enhanceTable(table);
      }
    }
  };

  function enhanceTable(table) {
    // Skip tables inside newsletter - they should remain basic/unstyled for email compatibility
    if (table.closest('.newsletter-wrapper') || 
        table.classList.contains('newsletter-wrapper') ||
        table.closest('.newsletter-item') || 
        table.classList.contains('newsletter-item')) {
      return;
    }

    ensureTableSemantics(table);
    const wrapper = createResponsiveWrapper(table);
    applyIntrinsicWidth(table);
    addFullscreenButton(wrapper, table);
    addScrollIndicators(wrapper.querySelector('.table-responsive-scroll'));
  }

  /**
   * Ensure table has proper semantic HTML structure.
   */
  function ensureTableSemantics(table) {
    // Add scope attributes to header cells
    for (const th of table.querySelectorAll('thead th:not([scope])')) th.setAttribute('scope', 'col');
    for (const th of table.querySelectorAll('tbody th:not([scope])')) th.setAttribute('scope', 'row');
  }

  function createResponsiveWrapper(table) {
    if (table.closest('.table-wrapper')) return table.closest('.table-wrapper');

    const wrapper = document.createElement('div');
    wrapper.className = 'table-wrapper';
    wrapper.innerHTML = `
      <div class="table-scroll-shadow table-scroll-shadow--left"></div>
      <div class="table-responsive-scroll"></div>
      <div class="table-scroll-shadow table-scroll-shadow--right"></div>
    `;

    table.parentNode.insertBefore(wrapper, table);
    wrapper.querySelector('.table-responsive-scroll').appendChild(table);
    return wrapper;
  }

  function applyIntrinsicWidth(table) {
    const DEFAULT_MIN_WIDTH = 600;
    const COLUMN_WIDTH_HINT = 160;

    // Align front-end rendering with widths authored in CKEditor while safeguarding readability.
    const explicitWidth = getExplicitTableWidth(table);
    const columnCount = getMaxColumnCount(table);
    const minWidthFromColumns = columnCount > 0 ? columnCount * COLUMN_WIDTH_HINT : 0;
    const computedMinWidth = Math.max(DEFAULT_MIN_WIDTH, minWidthFromColumns, explicitWidth || 0);

    table.style.setProperty('--table-min-width', `${Math.round(computedMinWidth)}px`);

    if (explicitWidth) {
      table.style.setProperty('--table-preferred-width', `${Math.round(explicitWidth)}px`);
    } else {
      table.style.removeProperty('--table-preferred-width');
    }
  }

  function getExplicitTableWidth(table) {
    return (
      parsePixelValue(table.style.width) ??
      parsePixelValue(table.getAttribute('width'), true) ??
      parsePixelValue(table.dataset.tableWidth, true) ??
      getColgroupWidth(table)
    );
  }

  function getColgroupWidth(table) {
    const cols = table.querySelectorAll('colgroup col');
    if (!cols.length) return null;

    let total = 0;

    for (const col of cols) {
      const width = parsePixelValue(col.style.width) ?? parsePixelValue(col.getAttribute('width'), true);
      if (!width) return null;
      total += width;
    }

    return total || null;
  }

  function getMaxColumnCount(table) {
    let maxColumns = 0;
    const rows = table.querySelectorAll('tr');

    for (const row of rows) {
      let columns = 0;
      for (const cell of row.cells) {
        columns += Number.parseInt(cell.getAttribute('colspan') || '1', 10);
      }
      maxColumns = Math.max(maxColumns, columns);
    }

    return maxColumns;
  }

  function parsePixelValue(value, allowUnitless = false) {
    if (!value) return null;

    const stringValue = String(value).trim();
    if (!stringValue) return null;

    if (stringValue.endsWith('px')) {
      const numeric = Number.parseFloat(stringValue);
      return Number.isFinite(numeric) ? numeric : null;
    }

    if (allowUnitless && /^[0-9]+(\.[0-9]+)?$/.test(stringValue)) {
      const numeric = Number.parseFloat(stringValue);
      return Number.isFinite(numeric) ? numeric : null;
    }

    return null;
  }

  function addFullscreenButton(wrapper, table) {
    const button = document.createElement('button');
    button.className = 'table-wrapper__fullscreen-btn';
    button.type = 'button';
    button.setAttribute('aria-label', Drupal.t('Open table in fullscreen'));
    button.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
      </svg>
      <span>${Drupal.t('Stor visning')}</span>
    `;
    button.onclick = () => openFullscreenModal(table);
    wrapper.insertBefore(button, wrapper.firstChild);
  }

  function addScrollIndicators(container) {
    const wrapper = container.closest('.table-wrapper');
    const leftShadow = wrapper.querySelector('.table-scroll-shadow--left');
    const rightShadow = wrapper.querySelector('.table-scroll-shadow--right');
    if (!leftShadow || !rightShadow) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const isScrollable = scrollWidth > clientWidth;

      leftShadow.classList.toggle('show', isScrollable && scrollLeft > 5);
      rightShadow.classList.toggle('hide', !isScrollable || scrollLeft >= scrollWidth - clientWidth - 5);
    };

    container.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    update();
  }

  function openFullscreenModal(table) {
    const modal = getOrCreateModal();
    const content = modal.querySelector('.table-fullscreen-modal__content');
    const captionEl = modal.querySelector('.table-fullscreen-modal__caption');

    content.innerHTML = '';
    content.appendChild(table.cloneNode(true));

    const caption = table.querySelector('caption');
    captionEl.textContent = caption?.textContent || table.getAttribute('aria-label') || Drupal.t('Tabellvisning');

    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    modal.querySelector('.table-fullscreen-modal__btn--close').focus();
    resetZoom(modal);
  }

  function getOrCreateModal() {
    let modal = document.querySelector('.table-fullscreen-modal');
    if (!modal) {
      modal = createFullscreenModal();
      document.body.appendChild(modal);
    }
    return modal;
  }

  function createFullscreenModal() {
    const modal = document.createElement('div');
    modal.className = 'table-fullscreen-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', Drupal.t('Table fullscreen view'));

    modal.innerHTML = `
      <div class="table-fullscreen-modal__header">
        <h2 class="table-fullscreen-modal__caption"></h2>
        <div class="table-fullscreen-modal__controls">
          <button type="button" class="table-fullscreen-modal__btn table-fullscreen-modal__btn--zoom-out" aria-label="${Drupal.t('Zoom out')}">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 13H5v-2h14v2z"/>
            </svg>
          </button>
          <span class="table-fullscreen-modal__zoom-level" aria-live="polite">100%</span>
          <button type="button" class="table-fullscreen-modal__btn table-fullscreen-modal__btn--zoom-in" aria-label="${Drupal.t('Zoom in')}">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
          </button>
          <button type="button" class="table-fullscreen-modal__btn table-fullscreen-modal__btn--reset" aria-label="${Drupal.t('Reset zoom')}">
            ${Drupal.t('Reset')}
          </button>
          <button type="button" class="table-fullscreen-modal__btn table-fullscreen-modal__btn--close" aria-label="${Drupal.t('Close fullscreen')}">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
            ${Drupal.t('Lukk')}
          </button>
        </div>
      </div>
      <div class="table-fullscreen-modal__content"></div>
    `;

    attachModalEventHandlers(modal);

    return modal;
  }

  function attachModalEventHandlers(modal) {
    modal.querySelector('.table-fullscreen-modal__btn--close').onclick = () => closeModal(modal);
    modal.querySelector('.table-fullscreen-modal__btn--zoom-in').onclick = () => adjustZoom(modal, 0.1);
    modal.querySelector('.table-fullscreen-modal__btn--zoom-out').onclick = () => adjustZoom(modal, -0.1);
    modal.querySelector('.table-fullscreen-modal__btn--reset').onclick = () => resetZoom(modal);
    modal.onkeydown = (e) => e.key === 'Escape' && closeModal(modal);
    modal.onclick = (e) => e.target === modal && closeModal(modal);
  }

  function closeModal(modal) {
    modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  function adjustZoom(modal, delta) {
    const content = modal.querySelector('.table-fullscreen-modal__content');
    const zoom = Math.max(0.5, Math.min(3, (Number.parseFloat(content.dataset.zoom) || 1) + delta));

    content.dataset.zoom = zoom;
    content.style.zoom = zoom;
    modal.querySelector('.table-fullscreen-modal__zoom-level').textContent = `${Math.round(zoom * 100)}%`;
  }

  function resetZoom(modal) {
    const content = modal.querySelector('.table-fullscreen-modal__content');
    const zoomLevel = modal.querySelector('.table-fullscreen-modal__zoom-level');

    content.dataset.zoom = '1';
    content.style.zoom = '1';
    zoomLevel.textContent = '100%';
  }

})(Drupal, once);
